// פרטים panel — the contact's reference data, as a scannable icon-row list:
// each row is a muted icon in a soft circular chip, a caption label, and the
// value beneath it, inside an OverviewPanel. Below sm that panel's TabCard
// drops its chrome and renders as a flat divider-separated section, which is
// the page-wide mobile convention.
//
// ONE treatment at every breakpoint, on purpose. A chrome-less "definition
// list" variant for the desktop reference column was tried and reverted: the
// icons and the card background are what make this list scannable, and losing
// them on the widest screen (while the phone kept them) was the wrong way
// round.
//
// Empty core fields (אימייל / מקור / כתובת) render a quiet "הוסף…" affordance
// that opens EditContactDialog, so the panel stays a working surface — not just
// data. Still ports the valuable legacy fields (ContactTableNestedRowDetails):
// extra phones, ת"ז, address, tags, additionalNotes (shown as "על הלקוח", an
// in-place text input saved on blur), and the created/updated/last-bulk
// timestamps — the latter demoted to a single quiet footer line so they stop
// reading as rows.
//
// DIRECTION: labels are always RTL; every VALUE picks its own direction from
// its content via `valueDir` (see valueDirection.ts). Purely numeric values
// (ת״ז) get an explicit "ltr" — `dir="auto"` cannot help them, it has no strong
// character to resolve from and would inherit the page's RTL.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import {
  Add,
  AssignmentIndOutlined,
  AutoAwesomeOutlined,
  BadgeOutlined,
  CampaignOutlined,
  ChatOutlined,
  EditOutlined,
  Facebook,
  FlagOutlined,
  HistoryOutlined,
  HomeWorkOutlined,
  Instagram,
  LabelOutlined,
  LanguageOutlined,
  LocalOfferOutlined,
  MailOutline,
  NotesOutlined,
  PersonOutline,
  PhoneCallbackOutlined,
  PhoneOutlined,
  PlaceOutlined,
  SearchOutlined,
  SendOutlined,
  SmartToyOutlined,
  SupervisorAccountOutlined,
  ThumbUpOutlined,
  WarningAmberOutlined,
  WhatsApp
} from "@material-ui/icons";
import { alpha, useTheme } from "@material-ui/core/styles";
import { differenceInDays } from "date-fns";
import Label from "components/Label";
import PhoneNumberDisplay from "components/PhoneNumberDisplay";
import useAuth from "hooks/useAuth";
import useContactBook from "hooks/contacts/useContactBook";
import useContactWrites from "hooks/contacts/useContactWrites";
import useFirestore from "hooks/useFirestore";
import useOrgDirectory from "hooks/useOrgDirectory";
import useContactHandler from "hooks/crm/useContactHandler";
import CrmHandlerBadge from "components/_dashboard/crm/handler/CrmHandlerBadge";
import CrmAssignHandlerDialog from "components/_dashboard/crm/handler/CrmAssignHandlerDialog";
import { CRM_HANDLER_ADMIN_ONLY_HINT, canAssignHandler } from "components/_dashboard/crm/handler/crmHandlerAccess";
import useSettings from "hooks/useSettings";
import { usePropertyTypes } from "hooks/usePropertyTypes";
import { fToNow } from "utils/formatTime";
import { CONTACT_NOTES_MAX_LENGTH } from "utils/contacts/contactWriteLimits";
import { latestBulkMessageTime } from "utils/contacts/lastBulkMessage";
import { EditContactDialog } from "../../../modal/EditContactDialog";
import { useContactSurfaceDown } from "../../contactSurface";
import { InlineFieldEdit } from "./edit/InlineFieldEdit";
import { buildContactEditSubject } from "./edit/contactEditSubject";
import { canInlineEditContact } from "./edit/contactEditAccess";
import { OverviewPanel } from "./OverviewPanel";
import { valueDir } from "./valueDirection";
import {
  getContactOriginLabel,
  SYSTEM_CONTACT_ORIGINS
} from "../../../../../../@types/sharedSchema";
import type { CrmCellSubject, CrmEditableDimension } from "components/_dashboard/crm/table/edit/crmCellEditModel";
import type { RealEstateContact } from "../../../../../../@types/sharedSchema";
import type { LastTouchInfo } from "./overviewData";
import type { Theme } from "@material-ui/core/styles";
import type { ReactNode } from "react";

type OverviewDetailsPanelProps = {
  contact: RealEstateContact;
  lastTouch: LastTouchInfo | null;
  /** True while the very first activity/notes data is still on its way. */
  lastTouchLoading: boolean;
};

/** One row of the panel. */
type DetailRowSpec = {
  key: string;
  Icon: IconComponent;
  label: string;
  value: ReactNode;
};

const STALE_TOUCH_DAYS = 7;

type IconComponent = typeof FlagOutlined;

// Origin → icon. Keys are the canonical system-stamped values (sharedSchema)
// plus the contact modal's Hebrew quick-pick presets (contactOrigin is free
// text — anything unknown falls back to a generic label icon).
const ORIGIN_ICONS: Record<string, IconComponent> = {
  [SYSTEM_CONTACT_ORIGINS.aiCallTranscription]: AutoAwesomeOutlined,
  [SYSTEM_CONTACT_ORIGINS.webhook]: SendOutlined,
  [SYSTEM_CONTACT_ORIGINS.mcp]: ChatOutlined,
  [SYSTEM_CONTACT_ORIGINS.homelyBot]: SmartToyOutlined,
  [SYSTEM_CONTACT_ORIGINS.dashboardSearch]: SearchOutlined,
  [SYSTEM_CONTACT_ORIGINS.bulkMessagingLog]: CampaignOutlined,
  [SYSTEM_CONTACT_ORIGINS.whatsappImport]: WhatsApp,
  [SYSTEM_CONTACT_ORIGINS.manual]: EditOutlined,
  [SYSTEM_CONTACT_ORIGINS.clientIntakeForm]: AssignmentIndOutlined,
  // CONTACT_ORIGIN_PRESETS (utils/contacts/contactOrigin.ts) — free-text values.
  "וואטסאפ": WhatsApp,
  "פייסבוק": Facebook,
  "המלצה": ThumbUpOutlined,
  "שלט / באנר": FlagOutlined,
  "יד2 / מדלן": HomeWorkOutlined, // legacy merged preset — existing contacts still carry it
  "יד2": HomeWorkOutlined,
  "מדלן": HomeWorkOutlined,
  "אינסטגרם": Instagram,
  "אתר": LanguageOutlined,
  "שיחה נכנסת": PhoneCallbackOutlined,
  "בוט Webot": SmartToyOutlined
};

const originIconFor = (origin: string | undefined | null): IconComponent =>
  (origin && ORIGIN_ICONS[origin]) || LabelOutlined;

// The soft circular chip holding each row's muted icon.
const iconChipSx = (theme: Theme) => ({
  width: 36,
  height: 36,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  bgcolor: alpha(theme.palette.grey[500], 0.12),
  color: theme.palette.text.secondary
});

// Long values (emails pre-ellipsis, addresses, notes) must wrap, never
// overflow — the panel-wide convention.
//
// ⚠️ `display: inline-block` IS THE RTL FIX, not a typographic preference.
//
// Reported as: the ת״ז renders hard against the LEFT edge while every other value sits
// at the right. The chain is exact. `valueDir` returns an explicit "ltr" for a
// digits-only value (see valueDirection.ts — nine digits carry no strong character to
// resolve from), `Typography variant="body2"` renders a `<p>`, and the row's value
// column is `flexGrow: 1` — so the `<p>` stretches to the full row width. `text-align`
// is LOGICAL and resolves against the ELEMENT'S OWN direction, so on that box `start`
// means left. The value ends up at the far edge of a row whose label is at the near one.
//
// The fix is to stop the dir-carrier being a stretched block: an inline-block shrink-
// wraps its content, so it has no surplus width for `text-align` to act on, and the
// BLOCK around it keeps the page's RTL and places it at the reading-start edge. The
// digits are still an LTR island and still get `unicode-bidi: isolate` free from the UA
// `[dir]` rule. Exactly what `CrmPhoneText` (crm/row/CrmRowBits.tsx) already ships, and
// what the tags row below has always done with its inner span.
//
// ⚠️ AND NOT `textAlign`. `textAlign: "start"` is the CSS INITIAL value, so writing it
// changes nothing — two rows here did, which is why the bug read as unfixable. Writing
// `textAlign: "right"` instead would be worse than useless: stylis-plugin-rtl runs every
// emotion rule through cssjanus, which swaps the literal `right`/`left` tokens, so it
// ships as `left` under RTL and `right` under LTR — the inverse of the intent in both
// builds. `crmFormat.ts` carries the same warning for `direction`.
const valueSx = {
  fontWeight: 500,
  overflowWrap: "anywhere",
  display: "inline-block",
  maxWidth: "100%"
} as const;

function DetailRow({
  Icon,
  label,
  children
}: {
  Icon: IconComponent;
  label: string;
  children: ReactNode;
}): JSX.Element {
  const theme = useTheme();
  return (
    <Stack direction="row" alignItems="flex-start" sx={{ gap: 1.5 }}>
      <Box sx={iconChipSx(theme)}>
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ lineHeight: 1.5 }}
        >
          {label}
        </Typography>
        {children}
      </Box>
    </Stack>
  );
}

/**
 * ── THE TWO EDIT AFFORDANCES OF THIS PANEL ──────────────────────────────────────
 *
 * Both open the CRM's own single-field popup (`edit/InlineFieldEdit`), which is the same
 * component the contacts table opens on a cell click — not a copy of it. Asked for in
 * these words: *"I want us to be able to click on '+איזורים', '+הוסף אימייל' and all
 * others, and actually see a small popup to write the value — just like we have in the
 * table. If the field is already set, on click, we should have the ability to edit."*
 *
 * So an EMPTY field and a FILLED one are the same interaction with different chrome:
 * {@link AddValueButton} is the quiet dashed invitation, {@link EditableValue} wraps a
 * value that is already there. Both fall back to the full dialog when inline editing is
 * off, so no affordance ever becomes a dead click.
 *
 * `mx`/`my` pull the hit area back out to the row's own edges — the padding exists to
 * make a one-line value a comfortable target, and without the negative margins it would
 * indent the value away from every non-editable row beside it.
 */
type EditAffordanceProps = {
  subject: CrmCellSubject;
  dimension: CrmEditableDimension;
  /** The ROW's label — "אימייל", "כתובת". Keys the saving indicator and names the toast. */
  fieldLabel: string;
  /** False for a record the viewer does not own — see `edit/contactEditAccess.ts`. */
  canEdit: boolean;
  /** Where a click goes when inline editing is off: the full dialog. */
  onFallback: () => void;
};

function AddValueButton({
  label,
  subject,
  dimension,
  fieldLabel,
  canEdit,
  onFallback
}: EditAffordanceProps & { label: string }): JSX.Element {
  return (
    <InlineFieldEdit
      subject={subject}
      dimension={dimension}
      label={fieldLabel}
      /* SHRINK-WRAPPED, unlike the value rows below. This is a short pill, it carries no
         `overflowWrap: "anywhere"` text so it cannot collapse, and the saving veil is
         drawn over the wrapper — a block wrapper would spread that veil across the whole
         empty row for a control the width of two words. */
      display="inline"
      disabled={!canEdit}
      render={(open, isOpen) => (
        <ButtonBase
          onClick={canEdit ? open : onFallback}
          aria-label={label}
          sx={{
            gap: 0.5,
            px: 0.75,
            py: 0.5,
            mx: -0.75,
            my: -0.5,
            borderRadius: 1,
            typography: "body2",
            justifyContent: "flex-start",
            color: isOpen ? "primary.main" : "text.disabled",
            ...(isOpen ? { bgcolor: "action.hover" } : null),
            "&:hover": { color: "primary.main", bgcolor: "action.hover" }
          }}
        >
          <Add sx={{ fontSize: 15 }} />
          {label}
        </ButtonBase>
      )}
    />
  );
}

/**
 * A value that is already set, made editable in place.
 *
 * Renders as ordinary text until hovered, deliberately: this panel is read far more often
 * than it is edited, and turning nine values into nine buttons would make a reference list
 * look like a form. The affordance appears where the pointer is.
 */
function EditableValue({
  subject,
  dimension,
  fieldLabel,
  canEdit,
  onFallback,
  ariaLabel,
  children
}: EditAffordanceProps & { ariaLabel: string; children: ReactNode }): JSX.Element {
  if (!canEdit) {
    // No wrapper at all rather than a disabled button: a value the viewer cannot edit
    // should not gain a hover state that promises they can.
    return <>{children}</>;
  }
  return (
    <InlineFieldEdit
      subject={subject}
      dimension={dimension}
      label={fieldLabel}
      /* ⚠️ FULL WIDTH, and the reported bug is what settled it. A shrink-to-fit wrapper
         made כתובת render as "חי / פה" and a ת״ז break after seven digits, because
         `valueSx` carries `overflowWrap: "anywhere"` — which drops a text run's
         min-content width to one character. A block fills the row and the text wraps at
         the real edge. It also makes the whole line the click target, which is what a
         value row should be. */
      display="block"
      render={(open, isOpen) => (
        <ButtonBase
          onClick={open}
          aria-label={ariaLabel}
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            textAlign: "start",
            width: "100%",
            minWidth: 0,
            /* ⚠️ NO NEGATIVE MARGINS, unlike the table's editable cell.
               There they pull the hit area out to the cell's own edges and the cell clips
               whatever escapes. This trigger is `width: 100%` of a row that does NOT clip,
               so `mx: -0.75` would put its border box at [-6px, W-6px] — bleeding 6px into
               the icon gutter at the start and leaving a 6px dead strip at the end.
               `box-sizing: border-box` is global here, so the padding lives inside the
               100% and the highlight lines up with the row instead. */
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            ...(isOpen ? { bgcolor: "action.hover" } : null),
            "&:hover": { bgcolor: "action.hover" }
          }}
        >
          {children}
        </ButtonBase>
      )}
    />
  );
}

// The legacy free-text additionalNotes field, shown as "על הלקוח" and editable
// in place (users asked for a plain text input here instead of read-only text).
// Draft locally; write (through `useContactWrites`) only on blur, and only
// when the text actually changed. External writes (the AI call pipeline also
// fills this field) refresh the draft unless the user is mid-edit.
function AboutClientField({ contact, canEdit }: { contact: RealEstateContact; canEdit: boolean }): JSX.Element {
  const { updateContact } = useContactWrites();
  const stored = contact.additionalNotes ?? "";
  const [draft, setDraft] = useState(stored);
  const editingRef = useRef(false);

  useEffect(() => {
    if (!editingRef.current) setDraft(stored);
  }, [stored]);

  const commit = (): void => {
    editingRef.current = false;
    const next = draft.trim();
    if (next === stored) return;
    /**
     * ⚠️ NEVER WRITE A RECORD THE VIEWER DOES NOT OWN, and this guard is data protection
     * rather than politeness.
     *
     * A colleague's contact reaches this tab through `/contacts/get-details`, which
     * REDACTS free text for a `basic`-pool caller — `additionalNotes` arrives as the blur
     * placeholder. This field seeds its draft from what it was handed, so without the
     * guard a viewer who merely focused and left the box would write `****` over the
     * owner's real note, destroying it with a string that was never a note.
     *
     * The field is rendered read-only in that case (see the `canEdit` branch below), so
     * this is the second lock; it is here because the first one is a prop and props are
     * easier to get wrong than a check beside the write.
     */
    if (!canEdit) return;
    setDraft(next);
    /**
     * Through the server (`POST /api/v1/contacts/update`), via the write hook — and with the
     * contact's OWNER named.
     *
     * This tab is mounted by the CRM side panel, whose agency view can put a colleague's
     * record on screen. An edit that named no owner would address the VIEWER's own book: a
     * 404 at best, and with the old browser-direct upsert a phantom contact minted in the
     * viewer's book holding nothing but this note. The server intersects the named owner
     * with the caller's resolved scope, so naming one can never reach outside it.
     */
    const owner = (contact.userId ?? "").trim();
    updateContact(
      contact.id,
      { additionalNotes: next || null },
      owner ? { ownerUserId: owner } : undefined
    ).catch((err: unknown) => {
      console.error("Failed saving the contact's additionalNotes", err);
      // Put the stored value back. Keeping the unsaved draft on screen is a save that
      // silently did not happen — the field looks committed and the text is gone on
      // the next render that reaches this component fresh.
      setDraft(stored);
    });
  };

  return (
    <TextField
      fullWidth
      multiline
      size="small"
      minRows={1}
      maxRows={8}
      value={draft}
      // A record the viewer does not own is READ-ONLY here rather than absent: the note is
      // the first row of the panel and often the most useful thing on it, and hiding it
      // would say the colleague has none.
      disabled={!canEdit}
      placeholder={canEdit ? "הוסיפו מידע חופשי על הלקוח…" : ""}
      onFocus={() => {
        editingRef.current = true;
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      // DIRECTION: free text typed by the realtor — the value picks its own
      // direction (the panel convention). While empty, inherit the page's RTL
      // so the Hebrew placeholder sits at the start edge.
      //
      // MAXLENGTH mirrors the server's `MAX_TEXT_LENGTH`, which this field can now
      // actually reach: the save goes through POST /contacts/update, which REJECTS an
      // over-length note rather than truncating it. Stopping the keystroke is the only
      // version of that the realtor can act on — a 400 after typing tells them nothing
      // and loses the text. Measured across the mirrored corpus (47,726 contacts,
      // 11,406 with notes) the LONGEST note is 540 characters and p99 is 294, so this
      // bound is ~9x the real maximum and nobody should ever meet it.
      inputProps={{ dir: draft ? valueDir(draft) : undefined, maxLength: CONTACT_NOTES_MAX_LENGTH }}
      sx={{ mt: 0.5 }}
    />
  );
}

export function OverviewDetailsPanel({
  contact,
  lastTouch,
  lastTouchLoading
}: OverviewDetailsPanelProps): JSX.Element {
  const theme = useTheme();
  const { user } = useAuth();
  const { userSettings } = useFirestore();
  const orgDirectory = useOrgDirectory();
  const { themeDirection } = useSettings();
  const { propertyTypes } = usePropertyTypes();
  const isRTL = themeDirection === "rtl";
  // The SURFACE, not the window — this panel also renders in the CRM side panel, where a
  // viewport query answers about the 1440px screen the 420px column happens to sit on.
  const isNarrow = useContactSurfaceDown("sm");
  const [editOpen, setEditOpen] = useState(false);
  const openEdit = () => setEditOpen(true);

  /**
   * Inline editing, gated on OWNERSHIP — see `edit/contactEditAccess.ts`. Every affordance
   * below takes this bundle, so a row cannot accidentally be wired editable on a record
   * whose edits the viewer would never see refresh.
   */
  // The viewer's BOOK as well as their email: in a pooled agency the office's record is
  // "mine" through its holder list, not through `userId` (see `canInlineEditContact`).
  const { ownBookKey } = useContactBook();
  const canEdit = canInlineEditContact(contact, user?.email as string | undefined, ownBookKey);
  const subject = useMemo(() => buildContactEditSubject(contact), [contact]);
  const editable = (dimension: CrmEditableDimension, fieldLabel: string) => ({
    subject,
    dimension,
    fieldLabel,
    canEdit,
    onFallback: openEdit
  });

  // The document id IS the phone — never `callerPhoneNumber`, which is absent on ~90% of
  // contacts and disagrees with the id where it exists.
  const phone = contact.id || "";
  const additionalPhones = (contact.additionalPhoneNumbers ?? []).filter(Boolean);
  const originLabel = getContactOriginLabel(contact.contactOrigin);
  const OriginIcon = originIconFor(contact.contactOrigin);

  // Owner: this page shows the user's own contacts; when the denormalized
  // userId points at a teammate (org data), resolve their display name.
  //
  // ⚠️ RESOLVED THROUGH `useOrgDirectory`, NOT `FirestoreContext.organizationUsers`, WHICH
  // IS WRONG IN TWO MEASURED WAYS: it reads `organization.users` only, while membership is
  // `users ∪ admins` (three of 96 orgs list an admin ONLY under `admins`), and
  // `firestore.rules` gates the org document on `users` too — so an admins-only member
  // cannot read their own org at all and this row silently printed a raw email address.
  // `useOrgDirectory` resolves both server-side and is already in memory on this page.
  const myEmail = user?.email ?? "";
  const ownerEmail = contact.userId || myEmail;
  const ownerName =
    ownerEmail === myEmail
      ? userSettings?.ownerFullName || user?.displayName || myEmail
      : orgDirectory.getMember(ownerEmail)?.displayName || ownerEmail;

  /**
   * WHO THE AGENCY DESIGNATED TO HANDLE THIS PERSON.
   *
   * This is the one surface where the designation is both LABELLED and EDITABLE, which is
   * why the detail header above can drop its badge on a phone: at 360px that header line is
   * already the one the mobile layout exists to compress, and a bare face there answers
   * nothing that this row does not answer better.
   *
   * It opens the DIALOG rather than an `InlineFieldEdit`. Assigning is not a field write —
   * it can create a contact document in a colleague's book, and that consequence has to be
   * stated before it happens.
   */
  const { handler, setLocal: setLocalHandler } = useContactHandler(contact.id ?? null);
  const [assignOpen, setAssignOpen] = useState(false);
  // The agency this contact's designation belongs to. With several, the control is withheld
  // rather than guessing — `resolveOrgContext` refuses the same guess server-side.
  const assignableOrgId = handler?.orgId ?? (orgDirectory.orgs.length === 1 ? orgDirectory.orgs[0].id : null);
  const canAssign =
    canAssignHandler(
      {
        inAgency: orgDirectory.orgs.length > 0,
        isAdmin: orgDirectory.orgs.some((org) => org.id === assignableOrgId && org.isAdmin)
      },
      handler,
      myEmail
    ) && Boolean(assignableOrgId);

  const touchAgeDays = lastTouch ? differenceInDays(new Date(), lastTouch.time) : null;
  const isStaleTouch = touchAgeDays !== null && touchAgeDays >= STALE_TOUCH_DAYS;

  // Timestamps demoted to one quiet footer line (they're metadata, not rows).
  const metaParts: string[] = [];
  if (contact.createdTime) metaParts.push(`נוצר ${fToNow(contact.createdTime, isRTL)}`);
  if (contact.updatedTime) metaParts.push(`עודכן ${fToNow(contact.updatedTime, isRTL)}`);
  const lastBulkMessageTime = latestBulkMessageTime(contact);
  if (lastBulkMessageTime) {
    metaParts.push(`דיוור אחרון ${fToNow(lastBulkMessageTime, isRTL)}`);
  }
  const metaLine = metaParts.length > 0 ? metaParts.join(" · ") : null;

  const rows: DetailRowSpec[] = [
    {
      key: "additionalNotes",
      Icon: NotesOutlined,
      label: "על הלקוח",
      // FIRST row on purpose (product feedback 2026-08): the free-text "who is
      // this person" line is what a realtor wants before the structured fields.
      // Always rendered (even when empty) — it's an input, and an absent row
      // would leave no way to add the text in the first place.
      value: <AboutClientField contact={contact} canEdit={canEdit} />
    },
    {
      key: "phone",
      Icon: PhoneOutlined,
      label: "טלפון",
      // DIRECTION: PhoneNumberDisplay renders its own LTR cluster (direction:
      // ltr on the wrapper + dir="ltr" on the text) — a phone must never be
      // reordered by the RTL page, and that is already handled there.
      value: (
        <>
          {/* The one sanctioned phone renderer: localized format + flag for
              foreign numbers; desktop click = copy, touch = copy/call/whatsapp
              popover. Never hand-roll formatNational in CRM surfaces. */}
          <PhoneNumberDisplay phone={phone} variant="full" />
          {additionalPhones.length > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              sx={{ gap: 0.75, flexWrap: "wrap", mt: 0.5 }}
            >
              {additionalPhones.map((additionalPhone) => (
                <PhoneNumberDisplay
                  key={additionalPhone}
                  phone={additionalPhone}
                  variant="compact"
                />
              ))}
            </Stack>
          )}
        </>
      )
    },
    {
      key: "mail",
      Icon: MailOutline,
      label: "אימייל",
      // DIRECTION: always ltr — an address is ASCII by definition and its dots
      // and @ are neutrals that RTL would reorder.
      value: contact.mail ? (
        /* ⚠️ NOT A <Link> ANY MORE, and it cannot be one.
           `EditableValue` wraps its child in a ButtonBase, and an anchor nested inside a
           button is both invalid HTML and actively wrong here: a click would fire the
           button's onClick AND follow the href, so opening the editor would launch the
           mail client at the same time. The address is now plain text inside the edit
           target, and "send an email" — which nothing on this panel offered before — is
           not silently traded for it, because clicking the row was already the way to
           EDIT the value on every other row. */
        <EditableValue {...editable("mail", "אימייל")} ariaLabel="עריכת אימייל">
          {/* Same treatment as ת״ז below (`valueSx` + an explicit LTR `dir`), and for the
              same reason. A prior version kept an explicit `width: "100%"` here (to
              enable an ellipsis truncation) — a flex item's specified `width` is honored
              even when its own `display: inline-block` gets blockified by the flex
              container, so the box still stretched across the whole row. `text-align`'s
              "start" resolves against THIS element's own `dir="ltr"`, i.e. left, and a
              full-width box has nothing to align "left" against but its own far edge —
              exactly the ת״ז bug described at the top of this file. Dropping the width
              (and the ellipsis machinery it existed to serve) lets the box shrink to the
              address and land at the row's reading-start edge, wrapping instead of
              truncating — same as every other value on this panel. */}
          <Typography variant="body2" component="span" sx={valueSx} dir="ltr">
            {contact.mail}
          </Typography>
        </EditableValue>
      ) : (
        <AddValueButton label="הוסף אימייל…" {...editable("mail", "אימייל")} />
      )
    },
    {
      key: "origin",
      Icon: OriginIcon,
      label: "מקור",
      // DIRECTION: free text, usually Hebrew ("וואטסאפ") but can be a Latin
      // system value — auto, with the numeric fallback handled by valueDir.
      value: originLabel ? (
        <Stack direction="row" alignItems="center" sx={{ gap: 0.75, flexWrap: "wrap" }}>
          {/* ONLY THE CHIP IS THE TRIGGER. `contactOriginAdditionalInfo`, rendered
              beside it, is NOT in the server's write whitelist — a patch naming it is
              rejected by name and 400s the whole save — so it must not sit inside
              something that looks like its editor. */}
          <EditableValue {...editable("contactOrigin", "מקור")} ariaLabel="עריכת מקור הליד">
          <Box
            component="span"
            dir={valueDir(originLabel)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.375,
              borderRadius: 4,
              maxWidth: "100%",
              overflowWrap: "anywhere",
              bgcolor: alpha(theme.palette.info.main, 0.12),
              color: theme.palette.info.dark,
              typography: "caption",
              fontWeight: 600
            }}
          >
            <OriginIcon sx={{ fontSize: 14, flexShrink: 0 }} />
            {originLabel}
          </Box>
          </EditableValue>
          {contact.contactOriginAdditionalInfo && (
            <Typography
              variant="caption"
              color="text.secondary"
              dir={valueDir(contact.contactOriginAdditionalInfo)}
              sx={{ overflowWrap: "anywhere", minWidth: 0 }}
            >
              {contact.contactOriginAdditionalInfo}
            </Typography>
          )}
        </Stack>
      ) : (
        <AddValueButton label="הוסף מקור…" {...editable("contactOrigin", "מקור")} />
      )
    },
    {
      key: "lastTouch",
      Icon: HistoryOutlined,
      label: "מגע אחרון",
      // DIRECTION: not user data — fToNow + the channel label are generated in
      // the app's own language, so they inherit themeDirection. Forcing a dir
      // here would break the LTR build.
      value: lastTouch ? (
        <Stack direction="row" alignItems="center" sx={{ gap: 0.75, flexWrap: "wrap" }}>
          {/* Relative time is the signal — emphasized; channel secondary. */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: isStaleTouch ? "warning.dark" : "text.primary"
            }}
          >
            {fToNow(lastTouch.time, isRTL)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            · {lastTouch.label}
          </Typography>
          {isStaleTouch && (
            <Tooltip title={`ללא מגע ${touchAgeDays} ימים`}>
              <WarningAmberOutlined sx={{ fontSize: 16, color: "warning.main" }} />
            </Tooltip>
          )}
        </Stack>
      ) : lastTouchLoading ? (
        <Skeleton variant="text" width={120} />
      ) : (
        <Typography variant="body2" color="text.disabled">
          אין תיעוד עדיין
        </Typography>
      )
    },
    // ── The two "who" rows, adjacent and worded as a pair ──────────────────────
    // מטפל = RESPONSIBILITY (one person, agency-wide, set by an admin).
    // שמור אצל = POSSESSION (whose record this is).
    ...(orgDirectory.orgs.length > 0
      ? [
          {
            key: "handler",
            Icon: SupervisorAccountOutlined,
            label: "מתווך מטפל",
            value: handler ? (
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                <CrmHandlerBadge handler={handler} size="xs" variant="chip" reserve={false} />
                {canAssign && (
                  <Button size="small" variant="text" onClick={() => setAssignOpen(true)} sx={{ fontWeight: 700 }}>
                    שינוי
                  </Button>
                )}
              </Stack>
            ) : canAssign ? (
              <Button size="small" variant="text" onClick={() => setAssignOpen(true)} sx={{ fontWeight: 700, px: 0 }}>
                בחר מתווך מטפל…
              </Button>
            ) : (
              // There is room for a SENTENCE here, unlike a 60px table cell — so the
              // unassigned state explains itself instead of showing an empty seat. The
              // 27 admin-less agencies land here and would otherwise see a dead control.
              <Typography variant="body2" color="text.disabled" sx={valueSx}>
                {`אין מתווך מטפל · ${CRM_HANDLER_ADMIN_ONLY_HINT}`}
              </Typography>
            )
          }
        ]
      : []),
    {
      key: "owner",
      Icon: PersonOutline,
      label: "שמור אצל",
      // DIRECTION: a person's name — Hebrew, Latin, or the raw email fallback.
      // auto resolves all three correctly.
      value: (
        <Typography variant="body2" component="span" sx={valueSx} dir={valueDir(ownerName)}>
          {ownerName}
        </Typography>
      )
    },
    {
      key: "address",
      Icon: PlaceOutlined,
      label: "כתובת",
      // DIRECTION: free text. auto skips the leading house number ("4 דירה")
      // and resolves from the first letter, which is the correct reading.
      value: contact.address ? (
        <EditableValue {...editable("address", "כתובת")} ariaLabel="עריכת כתובת">
          <Typography variant="body2" component="span" sx={valueSx} dir={valueDir(contact.address)}>
            {contact.address}
          </Typography>
        </EditableValue>
      ) : (
        <AddValueButton label="הוסף כתובת…" {...editable("address", "כתובת")} />
      )
    },
    ...(contact.tehudatZehut
      ? [
          {
            key: "tehudatZehut",
            Icon: BadgeOutlined,
            label: "ת״ז",
            // DIRECTION: 9 digits — NO strong character, so dir="auto" would
            // inherit RTL and mirror any separator/sign. valueDir returns the
            // explicit "ltr" this needs.
            value: (
              <EditableValue {...editable("tehudatZehut", "ת״ז")} ariaLabel="עריכת ת״ז">
                <Typography
                  variant="body2"
                  /* A span, so the dir-carrier is not a `<p>` — `valueSx` makes it
                     shrink-wrap either way, but a paragraph per field value was always
                     the wrong element for a definition row. */
                  component="span"
                  sx={valueSx}
                  dir={valueDir(contact.tehudatZehut)}
                >
                  {contact.tehudatZehut}
                </Typography>
              </EditableValue>
            )
          } as DetailRowSpec
        ]
      : []),
    {
      key: "tags",
      Icon: LocalOfferOutlined,
      label: "תגיות",
      /**
       * ALWAYS RENDERED, unlike ת״ז two rows up — the one row whose spread-conditional was
       * dropped on this pass.
       *
       * A conditional row has no affordance when it is empty, so there was no way to add a
       * first tag from this panel at all: the row simply was not there. Tags are a core CRM
       * axis (they are a filter dimension, a bulk-edit target and a column in the table), so
       * "this contact has none yet" is worth a line and an invitation. ת״ז is a
       * signature-prefill field most contacts will never carry, and it stays conditional
       * for exactly that reason — the two are a deliberate pair, not an inconsistency.
       */
      // DIRECTION: per tag — Label doesn't forward `dir`, so the text
      // rides an inner span that carries it.
      value: (contact.tags ?? []).length > 0 ? (
        <EditableValue {...editable("tags", "תגיות")} ariaLabel="עריכת תגיות">
          <Stack direction="row" sx={{ gap: 0.5, flexWrap: "wrap", mt: 0.25 }}>
            {(contact.tags ?? []).map((tag) => (
              <Label key={tag} color="info" variant="ghost">
                <Box component="span" dir={valueDir(tag)}>
                  {tag}
                </Box>
              </Label>
            ))}
          </Stack>
        </EditableValue>
      ) : (
        <AddValueButton label="הוסף תגית…" {...editable("tags", "תגיות")} />
      )
    }
  ];

  return (
    <>
      <OverviewPanel
        title="פרטים"
        headerEnd={
          <Tooltip title="עריכת פרטי הלקוח">
            <IconButton size="small" onClick={openEdit} aria-label="עריכת פרטי הלקוח">
              <EditOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        }
      >
        {/* Comfortable rhythm: roomier on touch, slightly tighter on desktop. */}
        <Stack sx={{ gap: isNarrow ? 2.25 : 1.75 }}>
          {rows.map(({ key, Icon, label, value }) => (
            <DetailRow key={key} Icon={Icon} label={label}>
              {value}
            </DetailRow>
          ))}

          {metaLine && (
            <Box>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption" color="text.disabled" component="div">
                {metaLine}
              </Typography>
            </Box>
          )}
        </Stack>
      </OverviewPanel>

      {/* Mounted ON DEMAND — it used to render with `open={false}` on every contact open,
          so its whole subtree (a 2,000-line form, its listing pickers and their state) was
          constructed for every contact anybody looked at. It remains the route to every
          field no chip here represents, and the fallback for a record the viewer does not
          own.

          ⚠️ This does NOT save the property-type facet request. `usePropertyTypes()` is a
          hook and runs unconditionally at the top of this component whatever the dialog
          does; moving that cost would mean lifting the hook to the tab, which is a
          separate change. Claiming the saving here would be the comment lying about the
          line under it. */}
      {editOpen && (
        <EditContactDialog
          open
          contact={contact}
          propertyTypes={propertyTypes}
          onClose={() => setEditOpen(false)}
        />
      )}

      {/* Mounted only once opened, like the edit dialog above it — it pulls the agency
          roster and a holders lookup, and this panel renders on every contact page. */}
      {assignOpen && assignableOrgId && (
        <CrmAssignHandlerDialog
          open
          orgId={assignableOrgId}
          contactId={contact.id ?? ""}
          displayName={contact.contactFullName ?? null}
          handler={handler ?? null}
          onClose={() => setAssignOpen(false)}
          onAssigned={setLocalHandler}
        />
      )}
    </>
  );
}
